/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { clarity } from 'react-microsoft-clarity';
import { EpErrorPage } from '../components/EpErrorPage';
import { ROUTE_HOMEPAGE, ROUTE_SYSTEM_ACCESS } from '../constants/urlConstants';
import { signinRedirect, signinSilent } from '../services/authService';
import { httpServices } from '../services/httpServices';
import { getRedirectUrl, getUser, setRedirectUrl } from '../utils/auth';
import { getDefaultAccess, setDefaultAccess } from '../utils/defaultAccess';
import ErrorBoundary from './ErrorBoundary';
import { jwtDecode } from 'jwt-decode';
import { EpLoading } from '../components/EpLoading';

const { setAuthHeader } = httpServices;

const withAuth = (Component: any) => {
  const Auth = (props: any) => {
    const { i18n } = useTranslation();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const router = useRouter();

    // Estado para controlar se estamos no cliente e verificados
    const [isVerified, setIsVerified] = useState(false);

    useEffect(() => {
      // Esta função roda APENAS no navegador
      const checkAuth = async () => {
        let user = getUser(); // Agora seguro (tem window)
        const defaultAccess = getDefaultAccess(); // Agora seguro
        const redirectURL = getRedirectUrl();

        const tokenSystemAccess = searchParams.get('tsa');
        let decoded: any = null;
        if (tokenSystemAccess) {
          try {
            decoded = jwtDecode(tokenSystemAccess);
          } catch (e) {
            console.error('Erro ao decodificar token', e);
          }
        }

        const cultureCode = searchParams.get('CultureCode');
        const userDecoded = decoded && decoded?.email;

        const isSameUser =
          defaultAccess?.email === user?.email || userDecoded === user?.email;

        const decodedUserToken: any = user?.token
          ? jwtDecode(user?.token)
          : null;
        const tokenExpirationTime = decodedUserToken?.exp;
        const isTokenExpired =
          tokenExpirationTime &&
          new Date(tokenExpirationTime * 1000) < new Date();

        // --- LÓGICA DE CLARITY ---
        if (
          user &&
          user?.email &&
          (process.env.NEXT_PUBLIC_ENVIRONMENT === 'Production' ||
            process.env.NEXT_PUBLIC_ENVIRONMENT === 'Production-EU') &&
          clarity.hasStarted()
        ) {
          clarity.identify('identify', { userProperty: user?.email });
          clarity.setTag('custom-id', user?.email);
        }

        // --- LÓGICA DE LOGIN SILENCIOSO ---
        if (user && isTokenExpired) {
          await signinSilent();
          user = getUser();
        }

        // --- LÓGICA DE IDIOMA ---
        if (cultureCode && cultureCode !== i18n.language) {
          i18n.changeLanguage(cultureCode);
        }

        // --- LÓGICA DE ACESSO PADRÃO ---
        if (tokenSystemAccess) {
          await setDefaultAccess({ ...decoded });
        }

        // --- REDIRECT URL ---
        if (!user && !redirectURL && tokenSystemAccess) {
          setRedirectUrl(`${pathname}`);
        }

        // --- VERIFICAÇÕES DE ROTEAMENTO ---
        if (!user && tokenSystemAccess) {
          await signinSilent();
          user = getUser();
        }

        if (!user) {
          signinRedirect();
          return;
        }

        if (user && defaultAccess && !isSameUser) {
          router.push(ROUTE_SYSTEM_ACCESS);
          return;
        }

        if (user && !defaultAccess && !tokenSystemAccess) {
          router.push(ROUTE_SYSTEM_ACCESS);
          return;
        }

        // Se passou por tudo, seta o header e libera a renderização
        user && setAuthHeader(user?.token);
        setIsVerified(true);
      };

      checkAuth();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname, searchParams]); // Dependências do useEffect

    // Enquanto não verifica, mostra loading (evita erro de window e flash de tela)
    // if (!isVerified) {
    //   console.log('Renderizou n verified')
    //   return <EpLoading loadingId="auth-loading" isVisible={true} />;
    // }

    return (
      <ErrorBoundary
        fallback={
          <EpErrorPage
            errorPageStatus={500}
            errorPageButtonOnClickFunc={() => router.push(ROUTE_HOMEPAGE)}
            translations={{
              titleError401: 'Unauthorized access',
              messageError401:
                'Oops! It seems that you do not have access to this page.',
              titleError403: 'Access denied',
              messageError403:
                'You do not seem to be authorized to access this page.',
              titleError404: 'Resource not found',
              messageError404: `Oops! We're sorry, but we were not able to locate the page.`,
              messageErrorDefault: `This is unexpected, but don't worry.`,
              backToHomeButtonText: 'Back to the home page',
              titleErrorDefault: '',
            }}
          />
        }
      >
        <Component {...props} />
      </ErrorBoundary>
    );
  };

  return Auth;
};

export default withAuth;
